(() => {
  const tracker = window.PLInteractions;
  if (!tracker || tracker.siteReady) return;
  tracker.siteReady = true;
  const clean = value => String(value || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  const targetOf = event => event.target?.closest ? event.target : event.target?.parentElement;
  const guard = fn => (...args) => { try { fn(...args); } catch (_) {} };
  const on = (type, fn, capture = true) => document.addEventListener(type, guard(fn), capture);
  const sectionTypes = ['ux-hero', 'ux-built-slide', 'ux-built-around', 'ux-banner-freedom', 'ux-start-with', 'ux-restore', 'ux-research-designs', 'ux-banner-improving', 'ux-icon-text', 'ux-collapsible', 'ux-steps-grid', 'ux-setup', 'ux-cleaning-care', 'ux-how-they-feel', 'ux-getting-in-out', 'ux-movement-research'];
  const sectionSelector = sectionTypes.map(name => `.${name}`).join(',');
  const context = node => {
    const location = tracker.locationOf(node);
    const section = node.closest(sectionSelector);
    const sectionName = sectionTypes.find(name => section?.classList.contains(name))?.replace(/^ux-/, '').replaceAll('-', '_') || location;
    return { interaction_location: location, section_name: sectionName };
  };
  const send = (name, node, data = {}) => tracker.emit(name, { ...context(node), ...data });
  const destination = link => {
    try {
      const url = new URL(link.getAttribute('href'), location.origin);
      if (url.protocol === 'mailto:') return '/contact/email';
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      if (url.origin !== location.origin) return '';
      if (/^\/(?:account|customer_authentication|checkouts|return)(?:\/|$)/.test(url.pathname)) return '';
      return url.pathname + (/^#UxCollapsible-[\w-]+$/.test(url.hash) ? url.hash : '');
    } catch (_) { return ''; }
  };
  const linkLabel = link => {
    if (link.closest('predictive-search')) {
      const path = destination(link);
      return path.includes('/products/') ? path.split('/products/')[1] : 'Search result';
    }
    if (link.getAttribute('href')?.startsWith('mailto:')) return 'Email support';
    return clean(link.textContent || link.getAttribute('aria-label') || link.querySelector('img')?.alt);
  };
  const ctaSelector = '#MainContent a.ux-button, #MainContent a.product-ux-backed-design, #MainContent a.ux-start-with__colum-link, #MainContent .card-wrapper a[href*="/products/"], #MainContent a[href^="mailto:"], header a, header-drawer a, .header-wrapper a, footer a, .footer a, .c-sidebar-nav a, predictive-search a';
  const isProductLink = link => Boolean(link.closest('.card-wrapper, .ux-start-with__colum, predictive-search')) && destination(link).includes('/products/');
  const clickedLinks = new WeakSet();
  on('click', event => {
    const target = targetOf(event);
    if (!target) return;
    const link = target.closest(ctaSelector);
    if (link && !link.closest('.ux-complete, cart-complementary-products') && !clickedLinks.has(event)) {
      const path = destination(link);
      if (path && !(path === location.pathname && !link.getAttribute('href')?.includes('#'))) {
        clickedLinks.add(event);
        const data = { element_name: linkLabel(link), destination_path: path, action: 'click' };
        if (isProductLink(link)) {
          const handle = path.split('/products/')[1]?.split('#')[0];
          send('product_link_click', link, { ...data, product_handle: handle });
        } else if (link.closest('predictive-search')) {
          send('search_interaction', link, { ...data, element_name: 'Search result', action: 'result_select' });
        } else {
          send('cta_click', link, data);
        }
      }
    }
    const title = target.closest('.product__accordion-item .summary__title');
    if (title) {
      const item = title.closest('.product__accordion-item');
      const before = item.classList.contains('open');
      setTimeout(guard(() => {
        const after = item.classList.contains('open');
        if (before !== after) send('accordion_toggle', item, { element_name: clean(title.textContent), action: after ? 'open' : 'close' });
      }));
    }
    const summary = target.closest('.ux-collapsible summary, .product__accordion summary, .product-compatibility__details summary');
    if (summary) {
      const details = summary.closest('details');
      const before = details.open;
      setTimeout(guard(() => {
        if (before !== details.open) send('accordion_toggle', summary, { element_name: clean(summary.textContent), action: details.open ? 'open' : 'close' });
      }));
    }
    const sizeUnit = target.closest('ux-size-code [data-code], ux-size-code2 [data-code]');
    if (sizeUnit && !sizeUnit.classList.contains('active')) {
      const data = { ...tracker.product(sizeUnit), element_name: 'Size format', action: 'unit_select', option_value: clean(sizeUnit.dataset.code) };
      setTimeout(guard(() => { if (sizeUnit.classList.contains('active')) send('size_guide_interaction', sizeUnit, data); }));
    }
    const sizeTab = target.closest('.ux-findsize [data-size]');
    if (sizeTab && !sizeTab.classList.contains('is-active')) {
      const label = clean(sizeTab.textContent);
      setTimeout(guard(() => { if (sizeTab.classList.contains('is-active')) send('size_guide_interaction', sizeTab, { element_name: label, action: 'product_tab' }); }));
    }
  });

  const optionIntent = new WeakSet();
  const markOption = event => {
    const target = targetOf(event);
    const input = target?.matches('input, select') ? target : target?.closest('label')?.control;
    if (input?.closest('variant-selects, product-form, .product-form__quantity')) optionIntent.add(input);
  };
  on('click', markOption);
  on('keydown', markOption);
  on('change', event => {
    const input = targetOf(event);
    if (!input || (!event.isTrusted && !optionIntent.has(input))) return;
    optionIntent.delete(input);
    const picker = input.closest('variant-selects');
    if (picker && input.matches('input[type="radio"], select')) {
      const field = input.closest('fieldset, .product-form__input');
      const label = field?.querySelector('.form__label, legend, label');
      const name = clean(input.dataset.optionName || input.selectedOptions?.[0]?.dataset.optionName || label?.textContent?.split(':')[0] || input.name.replace(/-\d+$/, '')); 
      send('product_option_select', input, { ...tracker.product(input), element_name: name, option_value: clean(input.value), action: 'select' });
    } else if (input.name === 'quantity' && input.closest('product-form, .product-form__quantity') && !input.closest('cart-drawer')) {
      send('product_option_select', input, { ...tracker.product(input), element_name: 'Quantity', option_value: clean(input.value), action: 'select' });
    }
  });
  on('change', event => {
    const input = targetOf(event);
    if (!input?.closest('facet-filters-form') || !input.matches('select[name="sort_by"], input[type="checkbox"][name^="filter."]')) return;
    send('collection_filter', input, { element_name: input.name === 'sort_by' ? 'Sort' : clean(input.name), option_value: clean(input.value), action: input.name === 'sort_by' ? 'sort' : input.checked ? 'apply' : 'remove' });
  });

  const sliderIntent = new WeakMap();
  const sliderTimers = new WeakMap();
  const findSlider = node => node?.closest('slider-component, slideshow-component');
  const markSlider = (event, action) => {
    const slider = findSlider(targetOf(event));
    if (slider && slider.closest('.ux-built-slide, media-gallery')) sliderIntent.set(slider, { action, time: Date.now() });
  };
  on('pointerdown', event => markSlider(event, 'swipe'));
  on('touchstart', event => markSlider(event, 'swipe'));
  on('keydown', event => {
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) markSlider(event, 'keyboard');
  });
  on('click', event => {
    const target = targetOf(event);
    if (!target) return;
    const button = target.closest('.slider-dot, .slider-button');
    if (button && !button.disabled) markSlider(event, button.matches('.slider-dot') ? 'dot' : button.name === 'previous' ? 'arrow_previous' : 'arrow_next');
    const thumbnail = target.closest('media-gallery .thumbnail');
    if (thumbnail) {
      const gallery = thumbnail.closest('media-gallery');
      gallery.querySelectorAll('slider-component').forEach(slider => sliderIntent.delete(slider));
      send('product_gallery_interaction', thumbnail, { ...tracker.product(thumbnail), element_name: clean(thumbnail.getAttribute('aria-label') || thumbnail.querySelector('img')?.alt || 'Product image'), action: 'thumbnail' });
    }
    const zoom = target.closest('.product__media-toggle');
    if (zoom) setTimeout(guard(() => {
      if (document.querySelector('product-modal[open], product-media-modal[open], .product-media-modal[open]')) {
        send('product_gallery_interaction', zoom, { ...tracker.product(zoom), element_name: clean(zoom.getAttribute('aria-label') || zoom.textContent || 'Product image'), action: 'enlarge' });
      }
    }));
  });
  on('slideChanged', event => {
    const slider = event.target;
    const intent = sliderIntent.get(slider);
    if (!intent || Date.now() - intent.time > 2000) return;
    clearTimeout(sliderTimers.get(slider));
    sliderTimers.set(slider, setTimeout(guard(() => {
      sliderIntent.delete(slider);
      const element = event.detail?.currentElement;
      const carousel = slider.closest('.ux-built-slide');
      const label = clean(element?.querySelector('.ux-built-slide__colum-title')?.textContent || element?.querySelector('img')?.alt || `Slide ${event.detail?.currentPage || ''}`);
      send(carousel ? 'carousel_interaction' : 'product_gallery_interaction', slider, { element_name: label, action: intent.action, slide_index: event.detail?.currentPage || 1 });
    }), 180));
  });

  const exposureNodes = new Map();
  const exposureKeys = new Set();
  const clearExposure = state => { clearTimeout(state.timer); state.timer = null; };
  const visible = node => {
    if (!node.isConnected || document.visibilityState === 'hidden' || node.closest('[hidden], [aria-hidden="true"]')) return false;
    if (node.closest('cart-drawer') && !node.closest('cart-drawer').classList.contains('active')) return false;
    if (node.closest('.ux-complete') && !node.closest('.ux-complete').classList.contains('active')) return false;
    const style = getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    const r = node.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    const h = Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0));
    const w = Math.max(0, Math.min(r.right, innerWidth) - Math.max(r.left, 0));
    return h >= Math.min(r.height, innerHeight) * .5 && w >= Math.min(r.width, innerWidth) * .5;
  };
  const updateExposure = (node, state) => {
    if (exposureKeys.has(state.key)) return;
    if (!tracker.allowed() || !visible(node)) { clearExposure(state); return; }
    if (state.timer) return;
    state.timer = setTimeout(guard(() => {
      state.timer = null;
      if (tracker.allowed() && visible(node) && send('section_view', node, state.data)) exposureKeys.add(state.key);
    }), 1000);
  };
  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = exposureNodes.get(entry.target);
      if (state) updateExposure(entry.target, state);
    });
  }, { threshold: [0, .1, .25, .5, .75, 1] }) : null;
  const observeExposure = (node, data) => {
    if (!observer || exposureNodes.has(node)) return;
    const key = JSON.stringify({ ...context(node), ...data });
    if (exposureKeys.has(key)) return;
    exposureNodes.set(node, { key, data, timer: null });
    observer.observe(node);
  };
  let exposureFrame = false;
  const refreshExposure = () => {
    if (exposureFrame) return;
    exposureFrame = true;
    requestAnimationFrame(() => {
      exposureFrame = false;
      exposureNodes.forEach((state, node) => {
        if (!node.isConnected || exposureKeys.has(state.key)) {
          clearExposure(state); observer?.unobserve(node); exposureNodes.delete(node);
        } else updateExposure(node, state);
      });
    });
  };
  on('scroll', refreshExposure);
  on('visibilitychange', refreshExposure);
  on('visitorConsentCollected', refreshExposure);

  const videoStates = new WeakMap();
  const userMedia = new WeakSet();
  const armVideo = event => {
    const node = targetOf(event);
    if (node?.matches('video')) userMedia.add(node);
    const deferred = node?.closest('deferred-media');
    if (deferred) userMedia.add(deferred);
  };
  on('pointerdown', armVideo);
  on('keydown', event => { if ([' ', 'Enter', 'k', 'K'].includes(event.key)) armVideo(event); });
  on('click', armVideo);
  const intentionalVideo = video => userMedia.has(video) || userMedia.has(video.closest('deferred-media'));
  const bindVideo = video => {
    if (videoStates.has(video)) return;
    const state = { started: false, milestones: new Set() };
    videoStates.set(video, state);
    const videoData = () => ({ element_name: clean(video.getAttribute('aria-label') || video.closest('[data-media-id]')?.querySelector('img')?.alt || context(video).section_name + ' video'), action: 'user_play', video_provider: 'html5' });
    const start = () => {
      if (!intentionalVideo(video) || state.started || !visible(video)) return false;
      if (send('video_start', video, videoData())) state.started = true;
      return state.started;
    };
    video.addEventListener('play', guard(start));
    video.addEventListener('timeupdate', guard(() => {
      if (!state.started) start();
      if (!state.started || !visible(video) || !Number.isFinite(video.duration) || video.duration <= 0) return;
      const percent = video.currentTime / video.duration * 100;
      [25, 50, 75].forEach(milestone => {
        if (percent >= milestone && !state.milestones.has(milestone) && send('video_progress', video, { ...videoData(), video_percent: milestone })) state.milestones.add(milestone);
      });
    }));
    video.addEventListener('ended', guard(() => {
      if (state.started && !state.milestones.has(100) && visible(video) && send('video_complete', video, { ...videoData(), video_percent: 100 })) state.milestones.add(100);
    }));
  };

  const newsletterKey = 'pl_newsletter_submission_v2';
  on('submit', event => {
    if (event.target.id !== 'ContactFooter' || !tracker.allowed()) return;
    try { sessionStorage.setItem(newsletterKey, String(Date.now())); } catch (_) {}
  });
  let newsletterSent = false;
  const newsletterSuccess = () => {
    const node = document.querySelector('#ContactFooter-success');
    if (!node || newsletterSent || !tracker.allowed()) return;
    try {
      const started = Number(sessionStorage.getItem(newsletterKey));
      if (!started || Date.now() - started > 1800000) return;
      if (send('newsletter_signup', node, { element_name: 'Footer newsletter', action: 'success' })) {
        newsletterSent = true;
        sessionStorage.removeItem(newsletterKey);
      }
    } catch (_) {}
  };
  const searchSeen = new WeakMap();
  const inspectSearch = () => {
    document.querySelectorAll('predictive-search[open][results]').forEach(search => {
      const term = search.querySelector('input[type="search"]')?.value.trim();
      const results = search.querySelectorAll('.predictive-search__results-list [role="option"]');
      if (!term || results.length || searchSeen.get(search) === term) return;
      if (send('search_interaction', search, { element_name: 'Predictive search', action: 'no_results' })) searchSeen.set(search, term);
    });
    const empty = document.querySelector('[data-pl-search-empty="true"]');
    if (empty && !searchSeen.has(empty) && send('search_interaction', empty, { element_name: 'Search results', action: 'no_results' })) searchSeen.set(empty, true);
  };
  const scan = () => {
    document.querySelectorAll('arc-accessory-offer').forEach(node => observeExposure(node, { section_name: 'arc_accessory_offer', element_name: 'Arc accessory offer', action: 'offer' }));
    document.querySelectorAll(sectionSelector).forEach(node => {
      const heading = node.querySelector('h2, .section-title');
      if (heading) observeExposure(heading, { element_name: clean(heading.textContent), action: 'section' });
    });
    document.querySelectorAll('#MainContent a.ux-button, #MainContent a.product-ux-backed-design').forEach(node => {
      const path = destination(node);
      if (path) observeExposure(node, { element_name: linkLabel(node), destination_path: path, action: 'cta' });
    });
    document.querySelectorAll('cart-complementary-products [data-recommended-product-id]').forEach(node => observeExposure(node, { ...tracker.product(node.querySelector('variant-selects') || node), element_name: tracker.product(node.querySelector('variant-selects') || node).product_handle, action: 'recommendation' }));
    const newsletter = document.querySelector('#ContactFooter');
    if (newsletter) observeExposure(newsletter, { element_name: 'Footer newsletter', action: 'form' });
    document.querySelectorAll('video').forEach(bindVideo);
    newsletterSuccess();
    inspectSearch();
    refreshExposure();
  };
  let scanTimer;
  const scheduleScan = () => {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(guard(scan), 80);
  };
  new MutationObserver(records => {
    if (records.some(record => record.type === 'childList' && [...record.addedNodes].some(node => node.nodeType === 1) || record.type === 'attributes' && record.target.matches('cart-drawer, predictive-search, .ux-complete'))) scheduleScan();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'results', 'class'] });
  on('visitorConsentCollected', scheduleScan);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', guard(scan), { once: true });
  else scan();
})();
